import { FC, useMemo } from 'react'
import clsx from 'clsx'
import PdDisplaySwiperPanel from '@components/pd-display-swiper-panel/pd-display-swiper-panel'
import { SwiperSlide } from 'swiper/react'
import { useMobileDetector } from '@hooks/use-mobile-detector'
import PdImage from '@components/pd-image/pd-image'
import {
  HeadingTag,
  IMultiCardTiling,
  Theme,
  paddingTopClassMap,
  paddingBottomClassMap,
} from '@common/pd-meta-components-types'
import TitleAndDesc from '@/components/standard/title-and-desc'
import SemanticText from '@/components/standard/semantic-text'

const DisplayCard: FC<{
  title: string
  mediaUrl: string
  theme?: Theme
  borderRadius?: number
  titleTag?: HeadingTag | null
}> = ({
  title,
  mediaUrl,
  theme,
  borderRadius,
  titleTag,
}) => {
  const bgColor = useMemo<{
    cardbg: string
    namebg: string
  }>(() => {
    switch (theme) {
      case Theme.Light:
      case Theme.Gray:
        return {
          cardbg: 'bg-[#E6E9ED]',
          namebg: 'bg-white',
        }

      case Theme.Dark:
      default:
        return {
          cardbg: 'bg-[#242424]',
          namebg: 'bg-[#1C1C1C]',
        }
    }
  }, [theme])

  return (
    <div className={['flex flex-col items-center w-full laptop:w-384px'].join(' ')}>
      <div
        className={clsx('w-full h-[105.3vw] laptop:h-452px overflow-hidden', bgColor.cardbg)}
        style={borderRadius ? { borderRadius: `${borderRadius}px` } : {}}
      >
        <PdImage src={mediaUrl} enableMask={false} object="cover" width={'100%'} height={'100%'} />
      </div>
      <div
        className={clsx(
          'w-full h-[25.3vw] px-3 laptop:h-112px laptop:px-6 flex items-center justify-center',
          bgColor.namebg
        )}
      >
        <SemanticText
          as={titleTag}
          defaultTag="span"
          className="text-center laptop:text-xl text-base font-semibold text-[var(--theme-text-color)]"
        >
          {title}
        </SemanticText>
      </div>
    </div>
  )
}

const MultiCardTiling: FC<IMultiCardTiling> = ({
  title,
  titleTag,
  itemTitleTag,
  desc,
  mediaGroup,
  theme,
  borderRadius,
  paddingTop = 'px120',
  paddingBottom = 'px120',
}) => {
  const isMobileDevice = useMobileDetector()
  // 判断是否需要导航空间：桌面端超过3张卡片，移动端超过1张卡片
  const needsNavSpace = isMobileDevice
    ? (mediaGroup?.length ?? 0) > 1
    : (mediaGroup?.length ?? 0) > 3

  return (
    <div
      className={clsx(
        'relative w-full overflow-hidden',
        paddingTopClassMap[paddingTop],
        paddingBottomClassMap[paddingBottom],
        // 仅当需要导航时才添加额外的底部 padding
        needsNavSpace ? 'pb-15 laptop:pb-60' : 'pb-15 laptop:pb-30',
        `pd-theme-${theme} bg-container-primary`
      )}
    >
      <TitleAndDesc title={title} desc={desc} titleTag={titleTag} />
      {mediaGroup?.length && (
        <div className={clsx('mx-auto', 'w-full', 'laptop:w-300', 'px-6 laptop:px-0')}>
          <PdDisplaySwiperPanel
            className="laptop:!overflow-visible"
            autoplay={{
              delay: 3000,
              disableOnInteraction: false,
            }}
            slidesPerGroup={1}
            loop={false}
            // loopedSlides={features?.length}
            grabCursor
            slidesPerView={isMobileDevice ? 1 : 'auto'}
            spaceBetween={isMobileDevice ? 0 : 24}
            pagination={
              !isMobileDevice
                ? {
                    el: '.pd-display-swiper-pagination',
                    clickable: true,
                    dynamicBullets: true,
                  }
                : {
                    clickable: true,
                  }
            }
            navigation={
              !isMobileDevice
                ? {
                    nextEl: '.pd-display-swiper-next',
                    prevEl: '.pd-display-swiper-prev',
                  }
                : false
            }
            elClassName={{
              elClassName: [
                'absolute w-full h-2 -bottom-8',
                'laptop:!w-300 laptop:h-25 laptop:mx-auto laptop:-bottom-25',
              ].join(' '),
              paginationClassName: clsx(
                'pd-display-swiper-pagination absolute flex items-center justify-center laptop:justify-start',
                'laptop:h-14 laptop:bottom-0',
                'pd-pagination-container'
              ),
              nextElClassName: clsx(
                'pd-display-swiper-next absolute cursor-pointer bg-center rotate-180',
                'bottom-0 right-0 laptop:h-14 laptop:w-14 bg-no-repeat bg-center bg-cover',
                theme === Theme.Dark
                  ? 'bg-white/20 bg-swiperBtn'
                  : 'bg-black/10 bg-blackSwiperBtn hover:bg-blackHoverSwiperBtn'
              ),
              prevElClassName: clsx(
                'pd-display-swiper-prev absolute cursor-pointer bg-center',
                'bottom-0 right-20 laptop:h-14 laptop:w-14 bg-no-repeat bg-center bg-cover',
                theme === Theme.Dark
                  ? 'bg-white/20 bg-swiperBtn'
                  : 'bg-black/10 bg-blackSwiperBtn hover:bg-blackHoverSwiperBtn'
              ),
            }}
            // slidesOffsetBefore={isMobileDevice ? 0 : (innerWidth - 1200) / 2}
            // slidesOffsetAfter={isMobileDevice ? 0 : (innerWidth - 1200) / 2}
          >
            {mediaGroup?.map((item, index) => (
              <SwiperSlide key={index} className="w-full laptop:!w-fit">
                <DisplayCard
                  {...item}
                  theme={theme}
                  borderRadius={borderRadius}
                  titleTag={itemTitleTag}
                />
              </SwiperSlide>
            ))}
          </PdDisplaySwiperPanel>
        </div>
      )}
    </div>
  )
}

export default MultiCardTiling
